import type { ChatItem } from "@/components/MessageBubble";
import { TOOL_CARD_KINDS } from "../components/cards/card-data";
import { classifyAppError, type AppErrorKind } from "../lib/app-errors";
import { isRecord } from "../lib/records";

type MessageStatus = "pending" | "success" | "failed";

type MessagePosition = {
  readonly index: number;
  readonly order?: number;
  readonly stepOrder?: number;
};

type ApprovalRequest = {
  readonly approvalId: string;
  readonly approvalGroupId: string;
  readonly toolName: string;
  readonly args: unknown;
  readonly messageId: string;
  readonly messageIndex: number;
  readonly position: MessagePosition;
};

type ApprovalResponse = {
  readonly approvalId: string;
  readonly approved: boolean;
  readonly reason?: string;
  readonly responseMessageId: string;
  readonly position: MessagePosition;
};

export type ParsedChatMessages = {
  readonly items: ChatItem[];
  readonly completedApprovalIds: readonly string[];
  readonly failedMessages: readonly FailedChatMessage[];
};

export type FailedChatMessage = {
  readonly messageId: string;
  readonly errorKind: AppErrorKind;
};

const GENERIC_FAILURE_MESSAGE = "I couldn't complete that request. Please try again.";
const CREDIT_FAILURE_MESSAGE =
  "Your AI credits have run out. Add credits or renew your subscription, then try again.";

function extractImageUrl(content: unknown): string | undefined {
  if (!Array.isArray(content)) return undefined;
  const imagePart = content.find(
    (part) =>
      isRecord(part) &&
      part.type === "image" &&
      typeof part.image === "string",
  );
  return isRecord(imagePart) && typeof imagePart.image === "string"
    ? imagePart.image
    : undefined;
}

function extractText(content: unknown): string {
  if (typeof content === "string") return content;
  if (!Array.isArray(content)) return "";
  return content
    .map((part) => {
      if (isRecord(part) && part.type === "text" && typeof part.text === "string") {
        return part.text;
      }
      return "";
    })
    .filter(Boolean)
    .join(" ");
}

function readStatus(message: Record<string, unknown>): MessageStatus | undefined {
  const status = message.status;
  if (status === "pending" || status === "success" || status === "failed") {
    return status;
  }
  return undefined;
}

function getPosition(
  message: Record<string, unknown>,
  index: number,
): MessagePosition {
  return {
    index,
    ...(typeof message.order === "number" ? { order: message.order } : {}),
    ...(typeof message.stepOrder === "number"
      ? { stepOrder: message.stepOrder }
      : {}),
  };
}

function isLaterPosition(candidate: MessagePosition, reference: MessagePosition): boolean {
  if (candidate.order !== undefined && reference.order !== undefined) {
    if (candidate.order !== reference.order) return false;
    if (candidate.stepOrder !== undefined && reference.stepOrder !== undefined) {
      return candidate.stepOrder > reference.stepOrder;
    }
  }
  return candidate.index > reference.index;
}

function findContinuationStatus(
  messages: readonly unknown[],
  responsePosition: MessagePosition,
): MessageStatus | undefined {
  let latestPosition: MessagePosition | undefined;
  let latestStatus: MessageStatus | undefined;

  messages.forEach((message, index) => {
    if (!isRecord(message)) return;
    const messageField = message.message;
    if (!isRecord(messageField) || messageField.role !== "assistant") return;
    const position = getPosition(message, index);
    if (!isLaterPosition(position, responsePosition)) return;
    if (latestPosition && !isLaterPosition(position, latestPosition)) return;
    const status = readStatus(message);
    if (status === undefined) return;
    latestPosition = position;
    latestStatus = status;
  });

  return latestStatus;
}

function getLatestResponse(
  approvalIds: readonly string[],
  responses: ReadonlyMap<string, ApprovalResponse>,
): ApprovalResponse | undefined {
  let latest: ApprovalResponse | undefined;
  for (const approvalId of approvalIds) {
    const response = responses.get(approvalId);
    if (!response) return undefined;
    if (!latest || isLaterPosition(response.position, latest.position)) {
      latest = response;
    }
  }
  return latest;
}

/**
 * Extracts the value from a persisted tool-result part. Stored outputs use a
 * typed envelope (`{ type: "json" | "text" | ..., value }`) but older rows may
 * carry a plain value or only a `result` field — all are accepted.
 */
function readToolResultValue(part: Record<string, unknown>): unknown {
  const output = part.output;
  if (isRecord(output) && typeof output.type === "string") {
    return output.value;
  }
  if (output !== undefined) return output;
  return part.result;
}

function collectToolResults(messages: readonly unknown[]): Map<string, unknown> {
  const results = new Map<string, unknown>();
  messages.forEach((message) => {
    if (!isRecord(message)) return;
    const messageField = message.message;
    if (!isRecord(messageField) || !Array.isArray(messageField.content)) return;
    messageField.content.forEach((part) => {
      if (
        !isRecord(part) ||
        part.type !== "tool-result" ||
        typeof part.toolCallId !== "string"
      ) {
        return;
      }
      results.set(part.toolCallId, readToolResultValue(part));
    });
  });
  return results;
}

export function parseChatMessages(messages: readonly unknown[]): ParsedChatMessages {
  const requests = new Map<string, ApprovalRequest>();
  const responses = new Map<string, ApprovalResponse>();
  const approvalIdsByGroup = new Map<string, string[]>();
  const failedMessages: FailedChatMessage[] = [];

  messages.forEach((message, messageIndex) => {
    if (!isRecord(message)) return;
    const messageField = message.message;
    if (!isRecord(messageField) || !Array.isArray(messageField.content)) return;
    const messageId =
      typeof message._id === "string" ? message._id : `msg-${messageIndex}`;
    const position = getPosition(message, messageIndex);

    if (messageField.role === "assistant") {
      const toolCalls = new Map<string, { readonly toolName: string; readonly args: unknown }>();
      messageField.content.forEach((part) => {
        if (
          isRecord(part) &&
          part.type === "tool-call" &&
          typeof part.toolCallId === "string"
        ) {
          toolCalls.set(part.toolCallId, {
            toolName: typeof part.toolName === "string" ? part.toolName : "tool",
            args: part.input ?? part.args,
          });
        }
      });
      messageField.content.forEach((part) => {
        if (
          !isRecord(part) ||
          part.type !== "tool-approval-request" ||
          typeof part.approvalId !== "string"
        ) {
          return;
        }
        const toolCall =
          typeof part.toolCallId === "string"
            ? toolCalls.get(part.toolCallId)
            : undefined;
        requests.set(part.approvalId, {
          approvalId: part.approvalId,
          approvalGroupId: messageId,
          toolName: toolCall?.toolName ?? "tool",
          args: toolCall?.args,
          messageId,
          messageIndex,
          position,
        });
        const groupIds = approvalIdsByGroup.get(messageId) ?? [];
        approvalIdsByGroup.set(messageId, [...groupIds, part.approvalId]);
      });
    }

    messageField.content.forEach((part) => {
      if (
        !isRecord(part) ||
        part.type !== "tool-approval-response" ||
        typeof part.approvalId !== "string" ||
        typeof part.approved !== "boolean"
      ) {
        return;
      }
      responses.set(part.approvalId, {
        approvalId: part.approvalId,
        approved: part.approved,
        ...(typeof part.reason === "string" ? { reason: part.reason } : {}),
        responseMessageId: messageId,
        position,
      });
    });
  });

  const completedApprovalIds = new Set<string>();
  const groupStatus = new Map<string, MessageStatus | undefined>();
  const groupResponseMessageIds = new Map<string, string>();
  const toolResults = collectToolResults(messages);
  approvalIdsByGroup.forEach((approvalIds, approvalGroupId) => {
    const latestResponse = getLatestResponse(approvalIds, responses);
    if (!latestResponse) return;
    groupResponseMessageIds.set(approvalGroupId, latestResponse.responseMessageId);
    const continuationStatus = findContinuationStatus(messages, latestResponse.position);
    groupStatus.set(approvalGroupId, continuationStatus);
    if (continuationStatus === "success") {
      approvalIds.forEach((approvalId) => completedApprovalIds.add(approvalId));
    }
  });

  const items: ChatItem[] = [];
  messages.forEach((message, messageIndex) => {
    if (!isRecord(message)) return;
    const messageId =
      typeof message._id === "string" ? message._id : `msg-${messageIndex}`;
    const messageField = message.message;
    if (!isRecord(messageField)) {
      if (readStatus(message) !== "failed") return;
      const errorKind = classifyAppError(message.error);
      failedMessages.push({ messageId, errorKind });
      items.push({
        kind: "text",
        role: "assistant",
        text:
          errorKind === "insufficient-credits"
            ? CREDIT_FAILURE_MESSAGE
            : GENERIC_FAILURE_MESSAGE,
        messageId,
        messageIndex,
      });
      return;
    }
    const role = messageField.role;
    const content = messageField.content;

    if (role === "user") {
      const text = extractText(content);
      const imageUrl = extractImageUrl(content);
      if (text.length > 0 || imageUrl !== undefined) {
        items.push({
          kind: "text",
          role: "user",
          text,
          messageId,
          messageIndex,
          ...(imageUrl !== undefined ? { imageUrl } : {}),
        });
      }
      return;
    }
    if (role !== "assistant") return;
    if (readStatus(message) === "failed") {
      const errorKind = classifyAppError(message.error);
      failedMessages.push({ messageId, errorKind });
      const text = extractText(content);
      items.push({
        kind: "text",
        role: "assistant",
        text:
          errorKind === "insufficient-credits"
            ? CREDIT_FAILURE_MESSAGE
            : text.length > 0
              ? text
              : GENERIC_FAILURE_MESSAGE,
        messageId,
        messageIndex,
      });
      return;
    }
    if (typeof content === "string") {
      if (content.length > 0) {
        items.push({
          kind: "text",
          role: "assistant",
          text: content,
          messageId,
          messageIndex,
        });
      }
      return;
    }
    if (!Array.isArray(content)) return;

    content.forEach((part) => {
      if (!isRecord(part)) return;
      if (part.type === "text") {
        const text = typeof part.text === "string" ? part.text : "";
        if (text.length > 0) {
          items.push({
            kind: "text",
            role: "assistant",
            text,
            messageId,
            messageIndex,
          });
        }
        return;
      }
      if (part.type === "tool-call") {
        const toolName = typeof part.toolName === "string" ? part.toolName : "tool";
        const args = part.input ?? part.args;
        const cardKind = TOOL_CARD_KINDS[toolName];
        if (cardKind !== undefined) {
          items.push({
            kind: "card",
            cardKind,
            toolName,
            args,
            result:
              typeof part.toolCallId === "string"
                ? toolResults.get(part.toolCallId)
                : undefined,
            messageId,
            messageIndex,
          });
          return;
        }
        items.push({
          kind: "tool-call",
          toolName,
          args,
          messageId,
          messageIndex,
        });
        return;
      }
      if (part.type !== "tool-approval-request" || typeof part.approvalId !== "string") {
        return;
      }
      const request = requests.get(part.approvalId);
      if (!request || completedApprovalIds.has(request.approvalId)) return;
      const response = responses.get(request.approvalId);
      const continuationStatus = groupStatus.get(request.approvalGroupId);
      const groupApprovalIds = approvalIdsByGroup.get(request.approvalGroupId) ?? [];
      const allGroupResponsesRecorded = groupApprovalIds.every((approvalId) =>
        responses.has(approvalId),
      );
      const approvalStatus = response
        ? !allGroupResponsesRecorded
          ? "recorded"
          : continuationStatus === "pending"
          ? "continuing"
          : continuationStatus === "failed" || continuationStatus === undefined
            ? "retry"
            : "recorded"
        : undefined;
      items.push({
        kind: "approval-request",
        approvalId: request.approvalId,
        approvalGroupId: request.approvalGroupId,
        toolName: request.toolName,
        args: request.args,
        messageId: request.messageId,
        messageIndex: request.messageIndex,
        ...(approvalStatus !== undefined ? { approvalStatus } : {}),
        ...(response
          ? {
              retryApproved: response.approved,
              responseMessageId:
                groupResponseMessageIds.get(request.approvalGroupId) ??
                response.responseMessageId,
              ...(continuationStatus === "pending" || continuationStatus === "failed"
                ? { continuationMessageStatus: continuationStatus }
                : {}),
            }
          : {}),
      });
    });
  });

  return {
    items,
    completedApprovalIds: [...completedApprovalIds],
    failedMessages,
  };
}

export function parseMessagesIntoItems(messages: readonly unknown[]): ChatItem[] {
  return parseChatMessages(messages).items;
}
